import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
const MAX_OVERRIDE_RATE = 1e9;
const TYPED_RATE_SCALE = 10;
const MEASURE_SCALE = 4;

// The server does this arithmetic in decimal on the digits it receives, rounding half away from zero. A
// binary value sits just below a half about as often as above it, so these work on the digits instead.
const toScaledDigits = value => {
  const _String$split = String(value).split('e'),
    _String$split2 = _slicedToArray(_String$split, 2),
    mantissa = _String$split2[0],
    _String$split2$ = _String$split2[1],
    exponent = _String$split2$ === void 0 ? '0' : _String$split2$;
  const _mantissa$split = mantissa.split('.'),
    _mantissa$split2 = _slicedToArray(_mantissa$split, 2),
    whole = _mantissa$split2[0],
    _mantissa$split2$ = _mantissa$split2[1],
    fraction = _mantissa$split2$ === void 0 ? '' : _mantissa$split2$;
  return {
    digits: BigInt(whole + fraction),
    scale: fraction.length - Number(exponent)
  };
};
const divideHalfAway = (n, divisor) => {
  const half = divisor / BigInt(2);
  return n < BigInt(0) ? -((-n + half) / divisor) : (n + half) / divisor;
};
const fromScaledDigits = (rounded, places) => {
  const sign = rounded < BigInt(0) ? '-' : '';
  const text = (sign ? -rounded : rounded).toString().padStart(places + 1, '0');
  return Number("".concat(sign).concat(text.slice(0, -places), ".").concat(text.slice(-places)));
};
const roundToPlaces = (value, places) => {
  if (!Number.isFinite(value)) return value;
  const _toScaledDigits = toScaledDigits(value),
    digits = _toScaledDigits.digits,
    scale = _toScaledDigits.scale;
  if (scale <= places) return value;
  return fromScaledDigits(divideHalfAway(digits, BigInt(10) ** BigInt(scale - places)), places);
};

/** A usage or read as the server stores it, at four decimals. */
export const toMeasure = value => roundToPlaces(value, MEASURE_SCALE);

/** The product rounded to the cent the way the server prices a usage charge. */
export const multiplyToCents = (a, b) => {
  if (!Number.isFinite(a) || !Number.isFinite(b)) return a * b;
  const x = toScaledDigits(a);
  const y = toScaledDigits(b);
  const scale = x.scale + y.scale;
  const product = x.digits * y.digits;
  const cents = scale <= 2 ? product * BigInt(10) ** BigInt(2 - scale) : divideHalfAway(product, BigInt(10) ** BigInt(scale - 2));
  return Number(cents) / 100;
};

/** Whether the manager left the rate cell empty, which clears any override rather than setting one. */
export const isRateCleared = value => value === null || value === undefined || typeof value === 'string' && value.trim() === '';

/**
 * A rate the manager typed, rounded to the ten decimals the server keeps, or null when there is nothing
 * to send.
 *
 * Null covers a cleared cell and anything the server would reject: the validators require the override
 * to be >= 0 and below 1,000,000,000, and a rejected row fails the whole save.
 */
export const toOverrideRate = value => {
  if (isRateCleared(value)) return null;
  const rate = Number(value);
  if (!Number.isFinite(rate) || rate < 0) return null;
  const typedRate = roundToPlaces(rate, TYPED_RATE_SCALE);
  return typedRate < MAX_OVERRIDE_RATE ? typedRate : null;
};

/**
 * The exact string to display for a rate cell. x-data-grid's default number formatter is
 * value.toLocaleString(), which rounds display to 3 decimals while the unrounded value still goes to
 * the server: typing 0.004521 would show 0.005 and still send 0.004521. Utility rates routinely carry
 * 4 to 6 decimals, so the cell shows exactly what was entered instead. Blank stays blank (String(null)
 * would print the word "null").
 */
export const formatMeterRate = value => isRateCleared(value) ? '' : String(value);
const MONEY_FORMAT = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD'
});
export const formatMoney = value => {
  if (value == null || String(value).trim() === '' || !Number.isFinite(Number(value))) return '';
  return MONEY_FORMAT.format(Number(value));
};

/**
 * The rate to show when a row is first parsed. Parse always returns meterRate: 0, a placeholder the
 * server replaces at save, never a rate anyone chose. Showing 0 as the starting value meant a manager
 * typing 0 matched the default and registered no edit at all; blank leaves 0 available as a real one.
 */
export const toInitialMeterRate = parsedMeterRate => parsedMeterRate === 0 ? null : parsedMeterRate;

/**
 * What a grid edit on the rate cell should do, given the cell's value before and after the edit and
 * whether the row was already marked overridden. Compares the grid's own before/after pair, taken
 * from the grid itself rather than the parsed CSV value, so a cell the manager never touched can
 * never register, no matter what its starting value looked like.
 */
export const resolveRateEdit = (oldMeterRate, newMeterRate, wasOverridden) => {
  if (oldMeterRate === newMeterRate) {
    return {
      isValid: true,
      isRateOverridden: wasOverridden
    };
  }
  const overrideRate = toOverrideRate(newMeterRate);
  if (!isRateCleared(newMeterRate) && overrideRate === null) {
    return {
      isValid: false,
      isRateOverridden: wasOverridden
    };
  }
  return {
    isValid: true,
    isRateOverridden: overrideRate !== null
  };
};

/**
 * Whether a Start read or End read cell holds no usable number. A typed 0 is a read; null, undefined, blank text
 * and anything that is not a finite number are not.
 */
export const isReadEmpty = value => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string' && value.trim() === '') return true;
  return !Number.isFinite(Number(value));
};
const toRead = value => isReadEmpty(value) ? null : Number(value);

/** Whether two read values are the same read: both empty, or the same number whether held as a number or as text. */
export const isSameRead = (a, b) => {
  const aEmpty = isReadEmpty(a);
  const bEmpty = isReadEmpty(b);
  return aEmpty || bEmpty ? aEmpty && bEmpty : Number(a) === Number(b);
};

/** Whether an edit changed either read of a row. */
export const haveReadsChanged = (newRow, oldRow) => !isSameRead(newRow.startMeterRead, oldRow.startMeterRead) || !isSameRead(newRow.endMeterRead, oldRow.endMeterRead);

/** The units with an empty start or end read, each named once, in row order. A unit with no name is ''. */
export const findUnitsMissingReads = rows => rows.reduce((names, row) => {
  var _row$unitName;
  const name = (_row$unitName = row.unitName) !== null && _row$unitName !== void 0 ? _row$unitName : '';
  const isMissing = isReadEmpty(row.startMeterRead) || isReadEmpty(row.endMeterRead);
  return isMissing && !names.includes(name) ? [...names, name] : names;
}, []);

/**
 * Usage as billing reads it: end minus start, subtracted on the digits and rounded half away from zero to four
 * decimals, or blank while either read is empty. A binary subtraction first would lose the last place
 * (1000.50005 minus 1000.5), and Math.round rounds a negative half toward zero.
 */
export const toUsageFromReads = (startMeterRead, endMeterRead) => {
  if (isReadEmpty(startMeterRead) || isReadEmpty(endMeterRead)) return '';
  const end = toScaledDigits(Number(endMeterRead));
  const start = toScaledDigits(Number(startMeterRead));
  const scale = Math.max(end.scale, start.scale, 0);
  const align = _ref => {
    let digits = _ref.digits,
      own = _ref.scale;
    return digits * BigInt(10) ** BigInt(scale - own);
  };
  const difference = align(end) - align(start);
  const rounded = scale > MEASURE_SCALE ? divideHalfAway(difference, BigInt(10) ** BigInt(scale - MEASURE_SCALE)) : difference * BigInt(10) ** BigInt(MEASURE_SCALE - scale);
  return fromScaledDigits(rounded, MEASURE_SCALE);
};

/** The Usage cell's text: up to the four decimals billing keeps, where the grid's default stops at three. */
export const formatUsage = value => value === null || value === undefined || value === '' || !Number.isFinite(Number(value)) ? '' : Number(value).toLocaleString('en-US', {
  maximumFractionDigits: MEASURE_SCALE
});
export const toSaveRows = parsedRows => parsedRows.map(row => {
  var _row$meterSerialNumbe, _row$startMeterDateOn, _row$endMeterDateOnly, _row$meterUsage, _row$unitsOfMeasure;
  const saveRow = {
    unitId: row.unitId || '',
    meterSerialNumber: (_row$meterSerialNumbe = row.meterSerialNumber) !== null && _row$meterSerialNumbe !== void 0 ? _row$meterSerialNumbe : '',
    startMeterRead: toRead(row.startMeterRead),
    endMeterRead: toRead(row.endMeterRead),
    startMeterDateOnly: (_row$startMeterDateOn = row.startMeterDateOnly) !== null && _row$startMeterDateOn !== void 0 ? _row$startMeterDateOn : '',
    endMeterDateOnly: (_row$endMeterDateOnly = row.endMeterDateOnly) !== null && _row$endMeterDateOnly !== void 0 ? _row$endMeterDateOnly : '',
    meterUsage: (_row$meterUsage = row.meterUsage) !== null && _row$meterUsage !== void 0 ? _row$meterUsage : 0,
    unitsOfMeasure: (_row$unitsOfMeasure = row.unitsOfMeasure) !== null && _row$unitsOfMeasure !== void 0 ? _row$unitsOfMeasure : ''
  };

  // Only rows the manager actually edited carry an override, and the key is omitted rather than
  // sent as null or 0 when there is none: the server prices this row from its own overrideRate when
  // present and falls back to the computed rate otherwise, so sending the calculated rate back would
  // freeze it.
  //
  // A manager can type different rates on both meters of a two-meter unit and both stick: the server
  // prices the override on the row it was typed on rather than grouping by unit. See
  // toSaveRows.test.js for the case this produces.
  if (row.isRateOverridden) {
    const overrideRate = toOverrideRate(row.meterRate);
    if (overrideRate !== null) saveRow.overrideRate = overrideRate;
  }
  return saveRow;
});
export const toUpdateRows = savedRows => savedRows.map(row => {
  var _row$overrideRate;
  return {
    id: row.id,
    startMeterRead: toRead(row.startMeterRead),
    endMeterRead: toRead(row.endMeterRead),
    meterUsage: Number(row.meterUsage) || 0,
    meterRate: Number(row.meterRate) || 0,
    baseCharge: Number(row.baseCharge) || 0,
    usageCharge: Number(row.usageCharge) || 0,
    allocationCharge: Number(row.allocationCharge) || 0,
    serviceDaysInPeriod: Number(row.serviceDaysInPeriod) || 0,
    overrideRate: (_row$overrideRate = row.overrideRate) !== null && _row$overrideRate !== void 0 ? _row$overrideRate : null
  };
});
const meterKeyOf = row => {
  var _row$meterSerialNumbe2;
  return "".concat(row.unitId, "::").concat((_row$meterSerialNumbe2 = row.meterSerialNumber) !== null && _row$meterSerialNumbe2 !== void 0 ? _row$meterSerialNumbe2 : '');
};
const readSignature = row => [row.startMeterRead, row.endMeterRead, row.startMeterDateOnly, row.endMeterDateOnly, row.unitsOfMeasure].map(value => value === null || value === undefined ? '' : String(value)).join('|');
export const findDivergentMeter = savedRows => {
  var _divergent$0$meterSer;
  const groups = savedRows.reduce((acc, row) => {
    var _acc$key;
    const key = meterKeyOf(row);
    acc[key] = [...((_acc$key = acc[key]) !== null && _acc$key !== void 0 ? _acc$key : []), row];
    return acc;
  }, {});
  const divergent = Object.values(groups).find(rows => rows.some(row => readSignature(row) !== readSignature(rows[0])));
  return divergent ? {
    unitName: divergent[0].unitName,
    meterSerialNumber: (_divergent$0$meterSer = divergent[0].meterSerialNumber) !== null && _divergent$0$meterSer !== void 0 ? _divergent$0$meterSer : ''
  } : null;
};
const withValue = (key, value) => value === null || value === undefined ? {} : {
  [key]: value
};
export const toRecalculateRequest = (savedRows, _ref2) => {
  var _savedRows$, _source$providerPhone, _source$billingAgentD, _source$leakServiceCo;
  let propertyId = _ref2.propertyId,
    utilityId = _ref2.utilityId,
    billingCycleId = _ref2.billingCycleId,
    allocationCycleId = _ref2.allocationCycleId;
  const seen = new Set();
  const rows = savedRows.reduce((acc, row) => {
    var _row$meterSerialNumbe3, _row$startMeterRead, _row$endMeterRead, _row$meterUsage2, _row$unitsOfMeasure2;
    const key = meterKeyOf(row);
    if (seen.has(key)) return acc;
    seen.add(key);
    acc.push(_objectSpread(_objectSpread(_objectSpread({
      unitId: row.unitId || '',
      meterSerialNumber: (_row$meterSerialNumbe3 = row.meterSerialNumber) !== null && _row$meterSerialNumbe3 !== void 0 ? _row$meterSerialNumbe3 : '',
      startMeterRead: (_row$startMeterRead = row.startMeterRead) !== null && _row$startMeterRead !== void 0 ? _row$startMeterRead : 0,
      endMeterRead: (_row$endMeterRead = row.endMeterRead) !== null && _row$endMeterRead !== void 0 ? _row$endMeterRead : 0
    }, withValue('startMeterDateOnly', row.startMeterDateOnly)), withValue('endMeterDateOnly', row.endMeterDateOnly)), {}, {
      meterUsage: (_row$meterUsage2 = row.meterUsage) !== null && _row$meterUsage2 !== void 0 ? _row$meterUsage2 : 0,
      unitsOfMeasure: (_row$unitsOfMeasure2 = row.unitsOfMeasure) !== null && _row$unitsOfMeasure2 !== void 0 ? _row$unitsOfMeasure2 : ''
    }));
    return acc;
  }, []);
  const source = (_savedRows$ = savedRows[0]) !== null && _savedRows$ !== void 0 ? _savedRows$ : {};
  return _objectSpread(_objectSpread(_objectSpread({
    propertyId,
    utilityId,
    billingCycleId,
    allocationCycleId
  }, withValue('userInitialCsvFileId', source.userInitialCsvFileId)), withValue('userEditedCsvFileId', source.userEditedCsvFileId)), {}, {
    acknowledgeAppr: true,
    cycleDisclosure: {
      providerPhone: (_source$providerPhone = source.providerPhone) !== null && _source$providerPhone !== void 0 ? _source$providerPhone : '',
      billingAgentDisclosure: (_source$billingAgentD = source.billingAgentDisclosure) !== null && _source$billingAgentD !== void 0 ? _source$billingAgentD : '',
      leakServiceContact: (_source$leakServiceCo = source.leakServiceContact) !== null && _source$leakServiceCo !== void 0 ? _source$leakServiceCo : ''
    },
    rows
  });
};